import { createAsyncThunk } from '@reduxjs/toolkit';
import internshipService from './Internship.service';
import {
  InternshipListParams,
  InternshipRemoveCandidateRequest,
  InternshipRequest,
} from './Internship.type';
import { errorHandlerThunk } from '../App/App.thunk';
import { ThunkDispatch } from 'redux-thunk';
import { PersonList } from '../Candidate/Candidate.type';
import { getInternshipListPeople } from './Internship.slice';
import { syncAttachmentsThunk } from '../Attachment/Attachment.thunk';
import { formatAttachmentsFiles } from '../../utils/formatAttachmentsFiles';
import { INTERNSHIP_ATTACH_OBJECT } from '../../constants/attachment.constant';
import { AxiosError } from 'axios';

type PersonListWithRegionId = PersonList & {regionId?: string};
type PersonIdWithRegionId = Pick<PersonList, 'id'> & {regionId?: string};
export type AddInternshipCandidateThunkPayload = PersonListWithRegionId | PersonIdWithRegionId;

export type ResponseData = {
  message?: string;
};

export const getInternshipMetaThunk = createAsyncThunk(
  'internship/getInternshipMetaThunk',
  async (_: undefined, thunkAPI) => {
    try {
      const response = await internshipService.getMeta();
      return response.data.meta;
    } catch (error) {
      throw thunkAPI.dispatch(errorHandlerThunk(error));
    }
  },
);

export const getInternshipListThunk = createAsyncThunk(
  'internship/getInternshipListThunk',
  async (params: InternshipListParams, thunkApi) => {
    try {
      const response = await internshipService.getList(params);
      return response.data;
    } catch (error) {
      throw thunkApi.dispatch(errorHandlerThunk(error));
    }
  },
);

export const getInternshipPeopleListThunk = createAsyncThunk(
  'internship/getInternshipPeopleListThunk',
  async ({ id, params = {} }: any, thunkApi) => {
    try {
      const response = await internshipService.getPeopleList(id, params);
      return response.data;
    } catch (error) {
      throw thunkApi.dispatch(errorHandlerThunk(error));
    }
  },
);

export const getInternshipInfoThunk = createAsyncThunk(
  'internship/getInternshipInfoThunk',
  async (id: number | string, thunkApi) => {
    try {
      const response = await internshipService.getInfo(id);
      return response.data.data;
    } catch (error) {
      thunkApi.dispatch(errorHandlerThunk(error));
    }
  },
);

export const createInternshipThunk = createAsyncThunk(
  'internship/createInternshipThunk',
  async (payload: InternshipRequest, { dispatch }): Promise<void> => {
    try {
      const {
        data: { data: result },
      } = await internshipService.create(payload);

      if (payload.attachments) {
        await dispatch(
          syncAttachmentsThunk(
            {
              type: INTERNSHIP_ATTACH_OBJECT,
              id: result.id,
            },
            formatAttachmentsFiles(payload.attachments),
          ),
        );
      }
    } catch (error) {
      dispatch(errorHandlerThunk(error));
    }
  },
);

export const removeCandidateInternshipThunk = createAsyncThunk(
  'internship/deleteCandidateInternshipThunk',
  async (
    payload: InternshipRemoveCandidateRequest,
    { dispatch },
  ): Promise<void> => {
    try {
      await internshipService.removeCandidate(
        payload.internshipId,
        payload.candidateId,
      );
      const { data } = await internshipService.getPeopleList(
        payload.internshipId,
        {} as any,
      );
      await dispatch(getInternshipInfoThunk(payload.internshipId));
      await dispatch(getInternshipListPeople(data));
    } catch (error) {
      dispatch(errorHandlerThunk(error));
    }
  },
);

export const updateInternshipThunk = createAsyncThunk(
  'internship/updateInternshipThunk',
  async (payload: InternshipRequest, { dispatch }): Promise<void> => {
    try {
      await internshipService.update(payload);

      if (payload.attachments) {
        await dispatch(
          syncAttachmentsThunk(
            {
              type: INTERNSHIP_ATTACH_OBJECT,
              id: payload.id,
            },
            formatAttachmentsFiles(payload.attachments),
          ),
        );
      }

      dispatch(getInternshipInfoThunk(payload.id!));
    } catch (error) {
      dispatch(errorHandlerThunk(error));
    }
  },
);

export const addInternshipCandidateThunk =
  ({
    id,
    payload,
  }: {
    id: string;
    payload: AddInternshipCandidateThunkPayload;
  }) =>
  (dispatch: ThunkDispatch<any, any, any>) => {
    return new Promise<
      AddInternshipCandidateThunkPayload | AxiosError<ResponseData, any>
    >(resolve => {
      internshipService
        .addPeople(id, payload)
        .then(() => {
          resolve(payload);
          dispatch(getInternshipInfoThunk(id!));
          dispatch(getInternshipPeopleListThunk({ id }));
        })
        .catch(e => {
          resolve(e);
        });
    });
  };

export const destroyInternshipThunk =
  (id: number | string) => (dispatch: ThunkDispatch<any, any, any>) => {
    return internshipService
      .destroy(id)
      .then(() => {
        return true;
      })
      .catch(err => {
        dispatch(errorHandlerThunk(err));
        return false;
      });
  };
